import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Markdown from 'markdown-to-jsx';
import React from 'react';
import EnmaeyaAvatar from '../chat_container/Avatar';
import Mermaid from '../Mermaid/Mermaid';
import './ChatMessage.css';

// Extract the raw text out of a markdown code block's children.
function codeText(children: React.ReactNode): string {
    if (typeof children === 'string') return children;
    return React.Children.toArray(children)
        .map(c => (typeof c === 'string' ? c : ''))
        .join('');
}

interface ChatMessageProps {
    isOpen: boolean;
    content: string;
    user: string;
    type: 'tool' | 'supervisor' | 'human';
    tool_name?: string;
    timestamp?: string;
    isStreaming?: boolean;
}

// Wrap markdown tables in a horizontally-scrollable container so wide tables
// (e.g. report listings) stay readable inside the narrow chat bubble.
const TableWrap: React.FC<any> = ({ children, ...props }) => (
    <div className="table-wrap">
        <table {...props}>{children}</table>
    </div>
);

// Render ```mermaid fenced blocks as diagrams; everything else stays a <code>.
// markdown-to-jsx tags fenced blocks with a `lang-<language>` class.
const CodeOrMermaid: React.FC<any> = ({ className, children, ...props }) => {
    if (typeof className === 'string' && /(^|\s)(lang|language)-mermaid(\s|$)/.test(className)) {
        return <Mermaid chart={codeText(children)} />;
    }
    return <code className={className} {...props}>{children}</code>;
};

// Open links the bot sends in a new tab (with safe rel to avoid tabnabbing).
const NewTabLink: React.FC<any> = ({ children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
);

const markdownOptions = {
    overrides: {
        table: { component: TableWrap },
        code: { component: CodeOrMermaid },
        a: { component: NewTabLink },
    },
};

// Hold back any trailing table block that hasn't finished streaming yet.
// Returns the portion of content safe to render mid-stream.
function visibleContent(content: string): string {
    // Hold back an unterminated fenced block (odd number of ``` fences) so a
    // diagram/code block still streaming isn't rendered half-formed.
    const fenceCount = (content.match(/^```/gm) || []).length;
    if (fenceCount % 2 === 1) {
        const lastFence = content.lastIndexOf('```');
        if (lastFence !== -1) return content.slice(0, lastFence);
    }

    const lines = content.split('\n');

    // Find last non-empty line
    let last = lines.length - 1;
    while (last >= 0 && lines[last].trim() === '') last--;

    // If last non-empty line isn't a table row, nothing to hold back
    if (last < 0 || !lines[last].trimStart().startsWith('|')) return content;

    // Find start of trailing table block
    let start = last;
    while (start > 0 && lines[start - 1].trimStart().startsWith('|')) start--;

    // If the table is followed by any non-table content it's already complete
    const hasContentAfter = lines
        .slice(last + 1)
        .some(l => l.trim() !== '' && !l.trimStart().startsWith('|'));
    if (hasContentAfter) return content;

    // Hold back the trailing table block; show everything before it
    return lines.slice(0, start).join('\n');
}

const ChatMessage: React.FC<ChatMessageProps> = ({ isOpen, user, content, type = 'supervisor', isStreaming = false }) => {
    const isUser = user === 'User';
    const isThinking = user === 'Thinking';
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Document layout: a single uniform surface — every turn shares the chat
    // background; user vs assistant is distinguished only by the avatar/author
    // header and the subtle divider between turns.
    const textColor = isDark ? '#e6e6e6' : '#26333d';
    // Always present the assistant as a single identity, regardless of which
    // internal agent (environment/health/analyser/…) produced the turn.
    const authorName = isUser ? 'You' : 'Enmaeya Agent';

    return (
        <Box
            className={`chat-message ${isUser ? 'user' : 'bot'} ${type === 'tool' ? 'tool' : ''}`}
            style={{ color: textColor }}
        >
            <div className="message-inner">
                {isUser ? (
                    // User: avatar sits beside the bubble on one row, no name label.
                    <div className="user-row">
                        <span className="message-avatar">
                            <span className="user-avatar">U</span>
                        </span>
                        <div className="message-text" dir="auto">
                            <Markdown options={markdownOptions}>{isStreaming ? visibleContent(content) : content}</Markdown>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="message-head">
                            <span className="message-avatar">
                                <EnmaeyaAvatar status={isOpen ? 'online' : 'offline'} />
                            </span>
                            <span className="message-author">{authorName}</span>
                        </div>
                        <div className={`message-text ${isThinking ? 'thinking-indicator' : ''}`} dir="auto">
                            <Markdown options={markdownOptions}>{isStreaming ? visibleContent(content) : content}</Markdown>
                        </div>
                    </>
                )}
            </div>
        </Box>
    );
};

export default ChatMessage;